import { useMemo } from 'react';
import { currentUserHasPermission } from '../Utils/Utilities';
import { getAuthData } from '../Utils/AuthUtils';

/**
 * Custom hook for filtering connections based on user permissions and business rules
 * @param {Array} connections - Array of connection objects to filter
 * @param {Object} options - Additional filtering options
 * @param {string} options.connectionTypeFilter - Filter by connection type ('C', 'S', or null)
 * @param {boolean} options.includeAddOption - Whether to include "add new" option in results
 * @param {Function} options.additionalFilter - Additional custom filter function
 * @returns {Object} - Filtered connections and metadata
 */
export const useConnectionFilter = (connections = [], options = {}) => {
  const {
    connectionTypeFilter = null,
    additionalFilter = null
  } = options;

  const authData = getAuthData();
  const currentUserID = authData?.UserID;

  // Get user permissions
  const canViewTypeID = currentUserHasPermission('ConnViewTypeID');
  const canAddConnections = currentUserHasPermission('ConnEntry');

  const filteredConnections = useMemo(() => {
    if (!Array.isArray(connections) || connections.length === 0) {
      return [];
    }

    let filtered = [...connections];

    // Exclude cold contacts
    // filtered = filtered.filter(connection => !(connection.IsCold === 1 || connection.IsCold === true));

    // Apply permission-based filtering
    if (canViewTypeID === 4) {
      // Show only connections where current user is assigned (POC)
      filtered = filtered.filter(connection => {
        const pocUserID = connection.POCUserID || connection.pocUserID;
        const thrPocIDs = connection.ThrPOCUserIDs ? connection.ThrPOCUserIDs.split(',') : [];
        const pocUserIDs = connection.POCUserIDs ? connection.POCUserIDs.split(',') : [];

        const isMainPOC = pocUserID && Number(pocUserID) === Number(currentUserID);
        const isInThreadPOC = thrPocIDs.some(id => Number(id.trim()) === Number(currentUserID));
        const isInPOCUserIDs = pocUserIDs.some(id => Number(id.trim()) === Number(currentUserID));

        return isMainPOC || isInThreadPOC || isInPOCUserIDs;
      });
    } else if (canViewTypeID === 2) {
      // Show only Customer connections (ConnType includes 'C')
      filtered = filtered.filter(connection => {
        const connType = connection.ConnType || connection.connTypes || '';
        return connType.includes('C') || connType.includes('P');
      });
    } else if (canViewTypeID === 3) {
      // Show only Supplier connections (ConnType includes 'S')
      filtered = filtered.filter(connection => {
        const connType = connection.ConnType || connection.connTypes || '';
        return connType.includes('S');
      });
    }
    // canViewTypeID === 1 or any other value shows all connections (no filtering)

    // Apply connection type filter if specified
    if (connectionTypeFilter) {
      filtered = filtered.filter(connection => {
        const connType = connection.ConnType || connection.connTypes || '';
        return connType.includes(connectionTypeFilter);
      });
    }

    // Apply additional custom filter if provided
    if (additionalFilter && typeof additionalFilter === 'function') {
      filtered = filtered.filter(additionalFilter);
    }

    return filtered;
  }, [connections, canViewTypeID, currentUserID, connectionTypeFilter, additionalFilter]);

  // Format connections for dropdown usage
  const connectionOptions = useMemo(() => {
    const options = filteredConnections
      .map(connection => ({
        value: connection.ConnID || connection.connID,
        label: connection.BizName || connection.bizName,
        connection
      }))
      .sort((a, b) => a.label?.localeCompare(b.label));



    return options;
  }, [filteredConnections, canAddConnections]);

  return {
    filteredConnections,
    connectionOptions,
    canAddConnections,
    canViewTypeID,
    hasPermissionRestrictions: canViewTypeID !== 1,
    totalConnections: connections.length,
    filteredCount: filteredConnections.length
  };
};

/**
 * Helper hook specifically for contract entry connection filtering
 * @param {Array} connections - Array of connection objects
 * @param {string} contractTypeSelection - 'purchase', 'sold', or null
 * @returns {Object} - Filtered connections for contract entry
 */
export const useContractConnectionFilter = (connections = [], contractTypeSelection = null) => {
  return useConnectionFilter(connections, {
    connectionTypeFilter: contractTypeSelection === 'purchase' ? 'S' :
      contractTypeSelection === 'sold' ? 'C' : null,
    additionalFilter: null
  });
};

/**
 * Helper hook specifically for general connection listing with search
 * @param {Array} connections - Array of connection objects
 * @param {string} selectedConnectionType - 'C', 'S', or 'all'
 * @param {string} searchTerm - Search term for filtering
 * @returns {Object} - Filtered connections for listing
 */
export const useConnectionListFilter = (connections = [], selectedConnectionType = 'all', searchTerm = '') => {
  const additionalFilter = useMemo(() => {
    if (!searchTerm.trim()) return null;

    const term = searchTerm.toLowerCase().trim();
    return (connection) => {
      return (
        (connection.contactName || connection.ContactName || '').toLowerCase().includes(term) ||
        (connection.bizName || connection.BizName || '').toLowerCase().includes(term) ||
        (connection.email || connection.Email || '').toLowerCase().includes(term) ||
        (connection.mobile || connection.Mobile || '').toLowerCase().includes(term) ||
        (connection.pocUserName || '').toLowerCase().includes(term)
      );
    };
  }, [searchTerm]);

  return useConnectionFilter(connections, {
    connectionTypeFilter: selectedConnectionType === 'all' ? null : selectedConnectionType,
    additionalFilter
  });
};